import { css } from '@emotion/react';
import { CgTranscript } from 'react-icons/cg';
import { FaPlay, FaPause } from 'react-icons/fa';
import { useState, useRef, useEffect, Fragment, memo, useCallback, useMemo } from 'react';
import { Modal, ProgressBar } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { fetchWrapper } from '@/_helpers';
import { VscLoading } from 'react-icons/vsc';
import OurAccordion from '@/_elements/OurAccordion';
import { LoadingSpinner } from '@/_elements/Loaders';

const timestampStyle = css({
    flexShrink: 0,
    color: '#333',
    marginRight: '0.5rem',
    borderBottom: '2px solid transparent',
    transition: 'all 0.2s ease-in-out',
    cursor: 'pointer',

    '&:hover': {
        color: '#397d7f',
        borderBottom: '2px solid #397d7f',
    },
});
function msToTimestamp(ms) {
    const date = new Date(ms);
    const h = String(date.getUTCHours()).padStart(2, '0');
    const m = String(date.getUTCMinutes()).padStart(2, '0');
    const s = String(date.getUTCSeconds()).padStart(2, '0');
    const msStr = String(ms % 1000).padStart(3, '0');
    return `${h}:${m}:${s}.${msStr}`;
}
function formatTranscript(data) {
    if (!data.segments) {
        return {
            text: data.text,
            language: data?.language,
        };
    }

    const lines = data.segments.map((segment) => ({
        contents: segment.text.trim(),
        startTime: msToTimestamp(segment.start * 1000),
        endTime: msToTimestamp(segment.end * 1000),
        startTimeMs: Math.floor(segment.start * 1000),
        endTimeMs: Math.floor(segment.end * 1000),
    }));

    return {
        lines,
        text: data.text,
        language: data?.language,
    };
}
const TranscriptProgress = memo(({ url, text = '', transcriptLines = [], show, onClose = () => {} }) => {
    const audioRef = useRef(null);
    const progressBarRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [currentLineIndex, setCurrentLineIndex] = useState(-1);
    const [showFullText, setShowFullText] = useState(false);

    const togglePlayPause = () => {
        if (!audioRef.current) return;
        if (isPlaying) {
            audioRef.current.pause();
        } else {
            audioRef.current.play();
        }
        setIsPlaying(!isPlaying);
    };

    const handleSeek = (e) => {
        if (!audioRef.current || !progressBarRef.current || duration === 0) return;

        const rect = progressBarRef.current.getBoundingClientRect();
        const clickPosition = e.clientX - rect.left;
        const percentage = Math.min(Math.max(clickPosition / rect.width, 0), 1);
        const seekTime = percentage * duration;

        audioRef.current.currentTime = seekTime;
        setCurrentTime(seekTime);

        if (!isPlaying) {
            audioRef.current.play();
            setIsPlaying(true);
        }
    };

    const handleLineClick = (line) => {
        if (!audioRef.current) return;
        const seekTime = line.startTimeMs / 1000;
        audioRef.current.currentTime = seekTime;
        setCurrentTime(seekTime);

        if (!isPlaying) {
            audioRef.current.play();
            setIsPlaying(true);
        }
    };

    const formatTime = (time) => {
        if (isNaN(time)) return '0:00';
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    };

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const onLoadedMetadata = () => {
            if (!isNaN(audio.duration)) {
                setDuration(audio.duration);
            }
        };

        const onTimeUpdate = () => {
            if (!audio || isNaN(audio.duration) || audio.duration === 0) return;
            setCurrentTime(audio.currentTime);

            const currentTimeMs = audio.currentTime * 1000;
            const idx = transcriptLines.findIndex(
                (line) => currentTimeMs >= line.startTimeMs && currentTimeMs <= line.endTimeMs
            );
            setCurrentLineIndex(idx);
        };

        const onEnded = () => {
            setIsPlaying(false);
            setCurrentLineIndex(-1);
        };

        audio.addEventListener('loadedmetadata', onLoadedMetadata);
        audio.addEventListener('timeupdate', onTimeUpdate);
        audio.addEventListener('ended', onEnded);

        return () => {
            audio.removeEventListener('loadedmetadata', onLoadedMetadata);
            audio.removeEventListener('timeupdate', onTimeUpdate);
            audio.removeEventListener('ended', onEnded);
        };
    }, [transcriptLines]);

    useEffect(() => {
        if (show) {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.currentTime = 0;
                setCurrentTime(0);
                setCurrentLineIndex(-1);
                audioRef.current.load();
            }
        } else {
            if (audioRef.current) {
                audioRef.current.pause();
                setIsPlaying(false);
            }
        }
    }, [show]);

    if (!url) return null;

    return (
        <Modal size="lg" centered show={show} onHide={onClose}>
            <Modal.Header closeButton>
                <Modal.Title>Transcript (auto-generated)</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="audio-player mb-4">
                    <audio ref={audioRef} src={url} preload="metadata" />
                    <div className="d-flex align-items-center mb-2">
                        <button
                            onClick={togglePlayPause}
                            className="btn btn-outline-primary me-3 d-flex align-items-center justify-content-center"
                            style={{ width: '40px', height: '40px' }}
                        >
                            {isPlaying ? <FaPause size={14} /> : <FaPlay size={14} className="ms-1" />}
                        </button>

                        <div className="d-flex align-items-center w-100" style={{ gap: '8px' }}>
                            <small className="text-muted" style={{ width: '40px', textAlign: 'right' }}>
                                {formatTime(currentTime)}
                            </small>

                            <div ref={progressBarRef} onClick={handleSeek} style={{ flexGrow: 1, cursor: 'pointer' }}>
                                <ProgressBar
                                    now={duration > 0 ? (currentTime / duration) * 100 : 0}
                                    style={{ height: '8px', width: '100%' }}
                                />
                            </div>

                            {/* Duration */}
                            <small className="text-muted" style={{ width: '40px', textAlign: 'left' }}>
                                {formatTime(duration)}
                            </small>
                        </div>
                    </div>
                </div>

                {/* Transcript */}
                <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                    {transcriptLines.map((line, idx) => {
                        const isActive = idx === currentLineIndex;
                        return (
                            <div
                                key={idx}
                                onClick={() => handleLineClick(line)}
                                className={`transcript-line ${isActive ? 'active-line' : ''}`}
                                style={{
                                    marginBottom: '0.5rem',
                                    padding: '0.5rem',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    backgroundColor: isActive ? '#e6eeed' : 'transparent',
                                    color: isActive ? '#397d7f' : '#333',
                                    fontWeight: isActive ? 'bold' : 'normal',
                                    borderLeft: isActive ? '4px solid #50aeb0' : '4px solid transparent',
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                <div className="d-flex">
                                    <span className="text-muted" css={timestampStyle}>
                                        {formatTime(line.startTimeMs / 1000)}
                                    </span>
                                    <span>{line.contents}</span>
                                </div>
                            </div>
                        );
                    })}
                </div>
                <br />
                <br />
                <div style={{ border: '1px solid #ccc' }}>
                    <OurAccordion title={<strong>Show Full Text</strong>} defaultShow={false}>
                        <p>{text}</p>
                    </OurAccordion>
                </div>
            </Modal.Body>
        </Modal>
    );
});
TranscriptProgress.displayName = 'TranscriptProgress';

export default function AudioPlayerWithTranscript({ url, transcriptions }) {
    const hasTranscription = useMemo(() => (transcriptions || [])?.length > 0, [transcriptions]);
    const [show, setShow] = useState(false);
    const [isBusy, setIsBusy] = useState(false);
    const [error, setError] = useState('');
    const handleShow = useCallback(() => setShow(true), []);
    const handleClose = useCallback(() => {
        setShow(false);
        setTranscriptionData({});
        setError('');
    }, []);
    const [transcriptionData, setTranscriptionData] = useState({});

    const fetchTranscription = useCallback(async () => {
        try {
            setError('');
            setIsBusy(true);
            setShow(true);
            if (hasTranscription) {
                const file = transcriptions[0].file;
                const response = await fetchWrapper.get(`file/${file}/transcriptions`);
                setTranscriptionData(formatTranscript(response?.data || response));
            } else {
                const response = await fetchWrapper.post(`file/transcriptions/get`, { url });
                setTranscriptionData(formatTranscript(response?.data || response));
            }
        } catch (e) {
            if (e.name === 'AbortError') return;
            toast.error(e.message);
            setError(e?.message || 'Error while fetching transcript');
        } finally {
            setIsBusy(false);
        }
    }, [show]);

    if (!url) return null;

    return (
        <Fragment>
            <div
                className="d-flex align-items-center mt-2"
                style={{ cursor: 'pointer' }}
                onClick={() => fetchTranscription()}
                disabled={isBusy}
            >
                {isBusy ? (
                    <VscLoading className="spin" />
                ) : (
                    <Fragment>
                        <CgTranscript className="icon  mr-2" size={25} />
                        View Transcript
                    </Fragment>
                )}
            </div>
            {show && (
                <Fragment>
                    {isBusy || error ? (
                        <Modal size="lg" centered show={true} onHide={() => setShow(false)}>
                            <Modal.Header closeButton>
                                <Modal.Title>Transcript (auto-generated)</Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                <div style={{ marginTop: '40px' }}></div>
                                <LoadingSpinner size={40} />
                                <div className="text-center" style={{ marginTop: '40px' }}>
                                    Fetching transcript...
                                    {error && <p className="text-danger">{error}</p>}
                                </div>
                            </Modal.Body>
                        </Modal>
                    ) : (
                        <TranscriptProgress
                            url={url}
                            text={transcriptionData?.text || ''}
                            transcriptLines={transcriptionData?.lines || []}
                            show={show}
                            onClose={handleClose}
                        />
                    )}
                </Fragment>
            )}
        </Fragment>
    );
}
